import React from 'react'
import { FormikActions } from 'formik'
import * as Yup from 'yup'
import { RouteComponentProps } from 'react-router-dom'
import { API } from '../../utils/api'

import { NotificationType } from '../../components/Notifications'
import { withNotificationContext } from '../../contexts/withNotificationContext'
import { SetNotification } from '../../contexts/NotificationProvider'
import { BatchTracingResults } from './BatchTracingResults'
import { Batch, BatchTracingForm, BatchTracingFormValues } from './BatchTracingForm'

interface Props extends RouteComponentProps<BatchTracingRouteParams> {
  setNotification: SetNotification
}

export interface BatchTracingRouteParams {
  searchByProperty: string
  searchByPropertyValue: string
}

interface State {
  batchGroups: Batch[]
  isFetchingData: boolean
}

interface BatchResponse {
  items: Batch[]
}

const charactersAndNumbersRegEx = /^[A-Za-z0-9]+$/

const ALLOWED_TYPES = ['batch', 'order']

class BatchTracingComponent extends React.Component<Props, State> {
  state: State = {
    batchGroups: [],
    isFetchingData: false,
  }

  searchTypes: { [key: string]: string } = {
    batch: 'Batch number',
    order: 'Order number',
  }

  formValidationSchema = Yup.object().shape({
    searchByProperty: Yup.string()
      .required('Search by option is required')
      .oneOf(Object.keys(this.searchTypes), 'Search by option is not valid'),
    searchByPropertyValue: Yup.string()
      .required('Batch Number or Order Number is required')
      .matches(charactersAndNumbersRegEx, { message: 'Batch Number or Order Number is not valid' }),
  })

  componentDidMount(): void {
    const searchByProperty = this.props.match.params.searchByProperty
    const searchByPropertyValue = this.props.match.params.searchByPropertyValue

    if (!searchByPropertyValue && !searchByProperty) return

    if (this.formValidationSchema.isValidSync({ searchByProperty, searchByPropertyValue })) {
      if (ALLOWED_TYPES.includes(searchByProperty)) {
        this.getBatchNumbers(searchByProperty, searchByPropertyValue)
      }
    } else {
      this.props.history.push('/batch-numbers')
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { searchByProperty, searchByPropertyValue } = this.props.match.params
    const {
      searchByProperty: prevSearchByProperty,
      searchByPropertyValue: prevSearchByPropertyValue,
    } = prevProps.match.params

    if (prevSearchByPropertyValue === searchByPropertyValue && prevSearchByProperty === searchByProperty) return

    if (!this.formValidationSchema.isValidSync({ searchByPropertyValue, searchByProperty })) {
      this.props.history.push('/batch-numbers')
      return
    }

    if (!ALLOWED_TYPES.includes(searchByProperty)) return

    this.getBatchNumbers(searchByProperty, searchByPropertyValue)
  }

  componentWillUnmount(): void {
    this.props.setNotification(null)
  }

  render() {
    const searchByProperty = this.props.match.params.searchByProperty
    const searchByPropertyValue = this.props.match.params.searchByPropertyValue
    return (
      <>
        <BatchTracingForm
          searchTypes={this.searchTypes}
          setNotification={this.props.setNotification}
          batchTracingFormValidationSchema={this.formValidationSchema}
          batchTracingFormInitialValues={{
            searchByProperty: searchByProperty || 'batch',
            searchByPropertyValue: searchByPropertyValue || '',
          }}
          handleSubmit={this.handleSubmit}
          isFetchingData={this.state.isFetchingData}
        />
        {!!this.state.batchGroups.length && searchByProperty && (
          <BatchTracingResults
            batchGroups={this.state.batchGroups}
            searchByProperty={searchByProperty}
            searchByPropertyValue={searchByPropertyValue}
          />
        )}
      </>
    )
  }

  private getBatchNumbers = (searchByProperty: string, searchByPropertyValue: string): void => {
    this.setState({ batchGroups: [] })
    this.fetchBatchNumbers(searchByProperty, searchByPropertyValue)
      .then((batchResponse: BatchResponse) => {
        const batchGroups = batchResponse.items
        this.setState({ batchGroups: batchGroups || [] })
        if (batchGroups.length === 0) {
          this.setState({ batchGroups: [] })
          this.props.setNotification({ text: 'No traces found', type: NotificationType.ERROR })
        }
      })
      .catch(() => {
        this.setState({ batchGroups: [] })
        this.props.setNotification({ text: 'No traces found', type: NotificationType.ERROR })
      })
  }

  private handleSubmit = (values: BatchTracingFormValues, actions: FormikActions<BatchTracingFormValues>): void => {
    actions.setSubmitting(false)
    this.props.history.push(`/batch-numbers/${values.searchByProperty}/${values.searchByPropertyValue}`)
  }

  private fetchBatchNumbers = (searchByProperty: string, searchByPropertyValue: string): Promise<BatchResponse> => {
    this.setState({ isFetchingData: true })
    return API.get(
      'its-batches',
      `/batches/${searchByProperty}No/${searchByPropertyValue.toUpperCase()}`,
      {},
    ).finally(() => this.setState({ isFetchingData: false }))
  }
}

export const BatchTracing = withNotificationContext(BatchTracingComponent)
